import { buildLevelSeedState } from "../data/level.seed";
import {
  CLASS_STORAGE_KEY,
  CLASS_STORAGE_KEY_LEGACY,
} from "../constants/class.constants";
import type { AdminClass } from "../types/class.types";

const LEGACY_LEVEL3_LABEL_TO_ID: Record<string, string> = Object.fromEntries(
  buildLevelSeedState().level3.map((l3) => [l3.label.toLowerCase(), l3.id])
);

function resolveLegacyLevel3Id(
  levels: AdminClass["levels"]
): string {
  if (levels.level3Id) return levels.level3Id;
  const label = levels.level3Label?.trim();
  if (!label) return "";
  return LEGACY_LEVEL3_LABEL_TO_ID[label.toLowerCase()] ?? "";
}

export function migrateAdminClass(record: AdminClass): AdminClass {
  const level3Id =
    record.levels.level3Id || resolveLegacyLevel3Id(record.levels);
  const seedL3 = buildLevelSeedState().level3.find((l) => l.id === level3Id);
  const uploadId =
    record.uploadId ||
    (record.thumbnailUrl ? `mock-upload-legacy-${record.id}` : "");
  const courseId = record.courseId || record.school?.linkedGradeIds?.[0] || "";

  return {
    ...record,
    uploadId: uploadId || record.uploadId,
    courseId,
    levels: {
      ...record.levels,
      level3Id,
      level3Label: record.levels.level3Label || seedL3?.label || level3Id,
    },
  };
}

export function loadMigratedClassStore(
  seed: AdminClass[]
): AdminClass[] {
  try {
    const rawV2 = localStorage.getItem(CLASS_STORAGE_KEY);
    if (rawV2) {
      const parsed = JSON.parse(rawV2) as AdminClass[];
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map(migrateAdminClass);
      }
    }
  } catch {
    /* fall through */
  }

  try {
    const rawV1 = localStorage.getItem(CLASS_STORAGE_KEY_LEGACY);
    if (rawV1) {
      const parsed = JSON.parse(rawV1) as AdminClass[];
      if (Array.isArray(parsed) && parsed.length > 0) {
        const migrated = parsed.map(migrateAdminClass);
        localStorage.setItem(CLASS_STORAGE_KEY, JSON.stringify(migrated));
        return migrated;
      }
    }
  } catch {
    /* use seed */
  }

  return seed.map(migrateAdminClass);
}

export function saveClassStore(classes: AdminClass[]): void {
  localStorage.setItem(CLASS_STORAGE_KEY, JSON.stringify(classes));
}
